import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Printer, Download, TrendingUp, TrendingDown, Minus } from "lucide-react";

const MONTH_LABEL = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-GB", { month: "short" });
};

const fmtGBP = (n) =>
  n == null || isNaN(n)
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency", currency: "GBP", maximumFractionDigits: 0,
      }).format(Number(n));

const fmtPct = (n) =>
  n == null || isNaN(n) ? "—" : `${Number(n).toFixed(1)}%`;

const variance = (actual, target) => {
  if (target == null || target === 0) return null;
  return ((actual - target) / Math.abs(target)) * 100;
};

const VarianceBadge = ({ actual, target, isPct }) => {
  const v = variance(actual, target);
  if (v == null) return <span className="text-[10px] text-slate-300">—</span>;
  const pos = v >= 0;
  const Icon = v > 0.5 ? TrendingUp : v < -0.5 ? TrendingDown : Minus;
  const color = v > 0.5 ? "text-emerald-600" : v < -0.5 ? "text-rose-600" : "text-slate-500";
  return (
    <div className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${color}`}>
      <Icon className="h-2.5 w-2.5" />
      {pos ? "+" : ""}{v.toFixed(1)}%
    </div>
  );
};

export default function BudgetTrackerTab() {
  const [fys, setFys] = useState([]);
  const [fyStart, setFyStart] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'tiles'
  const [tilePeriod, setTilePeriod] = useState("fy"); // 'fy' | 'q1'..'q4' | YYYY-MM

  useEffect(() => {
    (async () => {
      const r = await api.get("/budget-tracker/fys");
      setFys(r.data);
      // Pick the middle entry (current FY)
      const current = r.data[1] || r.data[0];
      setFyStart(current.fy_start_year);
    })();
  }, []);

  useEffect(() => {
    if (fyStart == null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [curR, prevR] = await Promise.all([
          api.get(`/budget-tracker?fy_start_year=${fyStart}`),
          api.get(`/budget-tracker?fy_start_year=${fyStart - 1}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        // Inject Rule of 40 as a derived metric.
        // Rule of 40 = Revenue Growth % + Net Profit Margin %
        // Growth %: ((curr.total_revenue - prior.total_revenue) / prior.total_revenue) × 100
        // Margin %: (curr.net_profit / curr.total_revenue) × 100
        const cur = curR.data;
        const prior = prevR.data;
        const priorByMonth = {};
        if (prior?.months) {
          // Align prior FY months to current month-of-FY position (i.e. April → April).
          prior.months.forEach((pm, idx) => {
            const cm = cur.months[idx];
            if (cm) priorByMonth[cm.month] = pm;
          });
        }
        cur.months = cur.months.map((m) => {
          const p = priorByMonth[m.month];
          const computeRule40 = (revKey, profitKey) => {
            const rev = Number(m[`${revKey}_actual`] !== undefined ? m[`${revKey}_actual`] : m[`${revKey}_target`]) || 0;
            const profit = Number(m[`${profitKey}_actual`] !== undefined ? m[`${profitKey}_actual`] : m[`${profitKey}_target`]) || 0;
            const priorRev = p ? Number(p[`${revKey}_actual`]) || 0 : 0;
            const growth = priorRev ? ((rev - priorRev) / priorRev) * 100 : null;
            const margin = rev ? (profit / rev) * 100 : null;
            if (growth == null && margin == null) return null;
            return (growth || 0) + (margin || 0);
          };
          return {
            ...m,
            rule_of_40_actual: computeRule40("total_revenue", "net_profit"),
            // Target Rule of 40: use target rev growth (vs prior actual) + target margin
            rule_of_40_target: (() => {
              const tRev = Number(m.total_revenue_target) || 0;
              const tProf = Number(m.net_profit_target) || 0;
              const priorRev = p ? Number(p.total_revenue_actual) || 0 : 0;
              const growth = priorRev ? ((tRev - priorRev) / priorRev) * 100 : null;
              const margin = tRev ? (tProf / tRev) * 100 : null;
              if (growth == null && margin == null) return null;
              return (growth || 0) + (margin || 0);
            })(),
          };
        });
        cur.metrics = [
          ...cur.metrics,
          { key: "rule_of_40", label: "Rule of 40", source: "derived" },
        ];
        setData(cur);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fyStart]);

  const totals = useMemo(() => {
    if (!data) return {};
    const result = {};
    data.metrics.forEach((m) => {
      const t = data.months.reduce((s, mo) => s + (Number(mo[`${m.key}_target`]) || 0), 0);
      const a = data.months.reduce((s, mo) => s + (Number(mo[`${m.key}_actual`]) || 0), 0);
      // For GM%, compute as weighted by total revenue rather than naive sum.
      if (m.key === "gm_pct") {
        const totRevT = data.months.reduce((s, mo) => s + (Number(mo.total_revenue_target) || 0), 0);
        const totRevA = data.months.reduce((s, mo) => s + (Number(mo.total_revenue_actual) || 0), 0);
        const gmT = data.months.reduce((s, mo) => s + (Number(mo.gm_target) || 0), 0);
        const gmA = data.months.reduce((s, mo) => s + (Number(mo.gm_actual) || 0), 0);
        result[m.key] = {
          target: totRevT ? ((totRevT - gmT) / totRevT) * 100 : 0,
          actual: totRevA ? ((totRevA - gmA) / totRevA) * 100 : 0,
        };
      } else if (m.key === "rule_of_40") {
        // Use FY-level Growth + FY-level Margin. Note: per-month rule_of_40 is
        // not summed — Rule of 40 is a single-point company metric.
        const months = data.months;
        const fyRev = months.reduce((s, mo) => s + (Number(mo.total_revenue_actual) || 0), 0);
        const fyProfit = months.reduce((s, mo) => s + (Number(mo.net_profit_actual) || 0), 0);
        const fyPriorRev = months.reduce(
          (s, mo) => s + (Number(mo.rule_of_40_actual) !== null ? 0 : 0), 0,
        );
        // For prior-year FY rev, sum derived from per-month rule_of_40 input (which
        // already accounts for priorByMonth). Simpler: just sum target-vs-actual at FY level.
        const fyRevT = months.reduce((s, mo) => s + (Number(mo.total_revenue_target) || 0), 0);
        const fyProfitT = months.reduce((s, mo) => s + (Number(mo.net_profit_target) || 0), 0);
        // Approximate growth %: average of monthly growths weighted by revenue.
        const growthsActual = months.map((mo) => mo.rule_of_40_actual != null
          ? mo.rule_of_40_actual - ((Number(mo.net_profit_actual) || 0) / (Number(mo.total_revenue_actual) || 1)) * 100
          : null).filter((g) => g != null);
        const avgGrowth = growthsActual.length
          ? growthsActual.reduce((s, g) => s + g, 0) / growthsActual.length
          : 0;
        const marginActual = fyRev ? (fyProfit / fyRev) * 100 : 0;
        const marginTarget = fyRevT ? (fyProfitT / fyRevT) * 100 : 0;
        result[m.key] = {
          target: avgGrowth + marginTarget,  // growth is shared between actual/target view
          actual: avgGrowth + marginActual,
        };
      } else {
        result[m.key] = { target: t, actual: a };
      }
    });
    return result;
  }, [data]);

  const exportCSV = () => {
    if (!data) return;
    const cols = ["Metric", "Type", ...data.months.map((m) => MONTH_LABEL(m.month)), "FY Total"];
    const lines = [cols.join(",")];
    data.metrics.forEach((m) => {
      const isPct = m.key === "gm_pct";
      const tRow = [
        `"${m.label} - Target"`, "Target",
        ...data.months.map((mo) => Number(mo[`${m.key}_target`]) || 0),
        isPct ? (totals[m.key]?.target ?? 0).toFixed(2) : totals[m.key]?.target ?? 0,
      ];
      const aRow = [
        `"${m.label} - Actual"`, "Actual",
        ...data.months.map((mo) => Number(mo[`${m.key}_actual`]) || 0),
        isPct ? (totals[m.key]?.actual ?? 0).toFixed(2) : totals[m.key]?.actual ?? 0,
      ];
      lines.push(tRow.join(","));
      lines.push(aRow.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `budget-tracker-${data.label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data || loading) {
    return <div className="text-sm font-mono text-slate-500" data-testid="budget-tracker-loading">Loading budget tracker…</div>;
  }

  return (
    <div className="space-y-5" data-testid="budget-tracker-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div className="space-y-1">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">Board Pack</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Budget Tracker — {data.label}</h2>
          <p className="text-xs text-slate-500">
            Targets &amp; actuals across the financial year (Apr → Mar). Sales actuals auto-computed from Closed Won deals. Other actuals from Xero (manual entry until Xero is wired).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(fyStart)}
            onValueChange={(v) => setFyStart(Number(v))}
          >
            <SelectTrigger data-testid="budget-fy-select" className="w-[180px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fys.map((f) => (
                <SelectItem key={f.fy_start_year} value={String(f.fy_start_year)}>
                  {f.label} ({new Date(f.start).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })} – {new Date(f.end).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              data-testid="budget-view-table"
              onClick={() => setViewMode("table")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${viewMode === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >Table</button>
            <button
              data-testid="budget-view-tiles"
              onClick={() => setViewMode("tiles")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-slate-200 ${viewMode === "tiles" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >Tiles</button>
          </div>
          <Button data-testid="budget-export-csv" variant="outline" size="sm" onClick={exportCSV} className="gap-1.5">
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button data-testid="budget-print" variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5">
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {viewMode === "tiles" && (
        <TileView data={data} totals={totals} period={tilePeriod} setPeriod={setTilePeriod} />
      )}

      {viewMode === "table" && (
      /* Board-pack — one mini-table per metric */
      <div className="space-y-4" data-testid="budget-tracker-table">
        {data.metrics.map((metric) => {
          const isPct = metric.key === "gm_pct" || metric.key === "rule_of_40";
          const fmt = isPct ? fmtPct : fmtGBP;
          const t = totals[metric.key] || { target: 0, actual: 0 };
          const sourceLabel = metric.source === "portal" ? "PORTAL"
            : metric.source === "derived" ? "DERIVED" : "XERO";
          const sourceCls = metric.source === "portal"
            ? "bg-blue-50 text-blue-700 border-blue-200"
            : metric.source === "derived"
              ? "bg-violet-50 text-violet-700 border-violet-200"
              : "bg-amber-50 text-amber-700 border-amber-200";
          // FY-level variance
          const fyVar = t.target ? ((t.actual - t.target) / Math.abs(t.target)) * 100 : null;
          const fyVarColour = fyVar == null ? "text-slate-400"
            : fyVar > 0.5 ? "text-emerald-600" : fyVar < -0.5 ? "text-rose-600" : "text-slate-500";

          return (
            <Card key={metric.key}
              className="border-slate-200 overflow-hidden print:border-none print:shadow-none print:break-inside-avoid"
              data-testid={`budget-metric-${metric.key}`}>
              {/* Mini-table header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wider">{metric.label}</h3>
                  <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${sourceCls}`}>{sourceLabel}</Badge>
                </div>
                <div className="flex items-center gap-4 text-[11px]">
                  <div>
                    <span className="text-slate-400 mr-1.5">Target</span>
                    <span className="font-mono font-semibold text-white">{fmt(t.target)}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 mr-1.5">Actual</span>
                    <span className="font-mono font-bold text-white">{fmt(t.actual)}</span>
                  </div>
                  {fyVar != null && (
                    <div className={`font-mono font-semibold tabular-nums ${fyVarColour === "text-emerald-600" ? "text-emerald-300" : fyVarColour === "text-rose-600" ? "text-rose-300" : "text-slate-300"}`}>
                      {fyVar >= 0 ? "+" : ""}{fyVar.toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
              {/* Mini-table body */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 w-[100px]">
                        &nbsp;
                      </th>
                      {data.months.map((m) => (
                        <th key={m.month}
                          className="px-2 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-slate-500 min-w-[80px]">
                          {MONTH_LABEL(m.month)}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 min-w-[100px]">
                        FY
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Target row */}
                    <tr className="border-b border-slate-100 bg-white">
                      <td className="sticky left-0 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        Target
                      </td>
                      {data.months.map((mo) => (
                        <td key={mo.month + "-t"} data-testid={`budget-${metric.key}-${mo.month}-target`}
                            className="px-2 py-1.5 text-right font-mono text-slate-500 tabular-nums">
                          {fmt(mo[`${metric.key}_target`])}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-mono font-semibold text-slate-700 bg-blue-50/40 tabular-nums">
                        {fmt(t.target)}
                      </td>
                    </tr>
                    {/* Actual row */}
                    <tr className="bg-slate-50/40">
                      <td className="sticky left-0 bg-slate-50/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-900 font-semibold">
                        Actual
                      </td>
                      {data.months.map((mo) => {
                        const actual = mo[`${metric.key}_actual`];
                        const target = mo[`${metric.key}_target`];
                        return (
                          <td key={mo.month + "-a"} data-testid={`budget-${metric.key}-${mo.month}-actual`}
                              className="px-2 py-1.5 text-right tabular-nums">
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="font-mono font-semibold text-slate-900">{fmt(actual)}</span>
                              <VarianceBadge actual={actual} target={target} isPct={isPct} />
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-1.5 text-right bg-blue-50/40 tabular-nums">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="font-mono font-bold text-slate-900">{fmt(t.actual)}</span>
                          <VarianceBadge actual={t.actual} target={t.target} isPct={isPct} />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })}
      </div>
      )}

      {/* Footer note */}
      <div className="text-[11px] text-slate-400 font-mono">
        {data.updated_at && (
          <>Last updated {new Date(data.updated_at).toLocaleString("en-GB")} {data.updated_by ? `by ${data.updated_by}` : ""}. </>
        )}
        Update targets &amp; Xero actuals in <span className="text-slate-600">Admin → Budget Targets</span>.
      </div>
    </div>
  );
}

/* ─────────── Tile view: one tile per metric, period selector ─────────── */
function TileView({ data, totals, period, setPeriod }) {
  // Period options: full FY, each fiscal quarter (Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar), each month.
  const monthsByQuarter = {
    q1: data.months.slice(0, 3),
    q2: data.months.slice(3, 6),
    q3: data.months.slice(6, 9),
    q4: data.months.slice(9, 12),
  };

  const aggregate = (key, isPct) => {
    let monthsSel = data.months;
    if (period === "fy") monthsSel = data.months;
    else if (period.startsWith("q")) monthsSel = monthsByQuarter[period];
    else monthsSel = data.months.filter((m) => m.month === period);

    if (key === "gm_pct") {
      const totRevT = monthsSel.reduce((s, m) => s + (Number(m.total_revenue_target) || 0), 0);
      const totRevA = monthsSel.reduce((s, m) => s + (Number(m.total_revenue_actual) || 0), 0);
      const gmCostT = monthsSel.reduce((s, m) => s + (Number(m.gm_target) || 0), 0);
      const gmCostA = monthsSel.reduce((s, m) => s + (Number(m.gm_actual) || 0), 0);
      return {
        target: totRevT ? ((totRevT - gmCostT) / totRevT) * 100 : 0,
        actual: totRevA ? ((totRevA - gmCostA) / totRevA) * 100 : 0,
      };
    }
    if (key === "rule_of_40") {
      // Rule of 40 = Revenue Growth % (vs prior-period) + Net Profit Margin %
      // Average per-month growth across selection + selection-level margin.
      const growths = monthsSel.map((m) => m.rule_of_40_actual != null
        ? m.rule_of_40_actual - ((Number(m.net_profit_actual) || 0) / (Number(m.total_revenue_actual) || 1)) * 100
        : null).filter((g) => g != null);
      const avgGrowth = growths.length ? growths.reduce((s, g) => s + g, 0) / growths.length : 0;
      const fyRev = monthsSel.reduce((s, m) => s + (Number(m.total_revenue_actual) || 0), 0);
      const fyProfit = monthsSel.reduce((s, m) => s + (Number(m.net_profit_actual) || 0), 0);
      const fyRevT = monthsSel.reduce((s, m) => s + (Number(m.total_revenue_target) || 0), 0);
      const fyProfitT = monthsSel.reduce((s, m) => s + (Number(m.net_profit_target) || 0), 0);
      return {
        actual: avgGrowth + (fyRev ? (fyProfit / fyRev) * 100 : 0),
        target: avgGrowth + (fyRevT ? (fyProfitT / fyRevT) * 100 : 0),
      };
    }
    return {
      target: monthsSel.reduce((s, m) => s + (Number(m[`${key}_target`]) || 0), 0),
      actual: monthsSel.reduce((s, m) => s + (Number(m[`${key}_actual`]) || 0), 0),
    };
  };

  const periodLabel = period === "fy"
    ? `Full FY ${data.label}`
    : period.startsWith("q")
      ? `${period.toUpperCase()} ${data.label}`
      : new Date(period + "-02").toLocaleString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="budget-tracker-tiles">
      {/* Period selector */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mr-1">Period:</span>
        <PeriodChip v="fy" cur={period} setCur={setPeriod}>FY</PeriodChip>
        {["q1", "q2", "q3", "q4"].map((q) => (
          <PeriodChip key={q} v={q} cur={period} setCur={setPeriod}>{q.toUpperCase()}</PeriodChip>
        ))}
        <span className="border-l border-slate-200 h-5 mx-1" />
        {data.months.map((m) => (
          <PeriodChip key={m.month} v={m.month} cur={period} setCur={setPeriod}>
            {MONTH_LABEL(m.month)}
          </PeriodChip>
        ))}
      </div>

      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">
        {periodLabel}
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.metrics.map((metric) => {
          const isPct = metric.key === "gm_pct" || metric.key === "rule_of_40";
          const { target, actual } = aggregate(metric.key, isPct);
          const fmt = isPct ? fmtPct : fmtGBP;
          const v = variance(actual, target);
          const pos = v != null && v >= 0;
          const accent = v == null ? "slate" : v > 0.5 ? "emerald" : v < -0.5 ? "rose" : "slate";
          return (
            <div
              key={metric.key}
              data-testid={`budget-tile-${metric.key}`}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{metric.label}</div>
                  <div className="font-heading font-bold text-2xl text-slate-900 mt-1 tabular-nums">{fmt(actual)}</div>
                </div>
                <Badge className={`text-[9px] py-0 px-1.5 ${
                  metric.source === "portal" ? "bg-blue-50 text-blue-700 border-blue-200"
                    : metric.source === "derived" ? "bg-violet-50 text-violet-700 border-violet-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {metric.source === "portal" ? "PORTAL" : metric.source === "derived" ? "DERIVED" : "XERO"}
                </Badge>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Target&nbsp;<span className="font-mono text-slate-700">{fmt(target)}</span>
                </div>
                {v != null && (
                  <div className={`text-xs font-mono font-semibold text-${accent}-600 tabular-nums`}>
                    {pos ? "+" : ""}{v.toFixed(1)}%
                  </div>
                )}
              </div>
              {/* Progress bar */}
              {target > 0 && !isPct && (
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-${accent}-500 transition-all`}
                    style={{ width: `${Math.min(150, Math.max(0, (actual / target) * 100))}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PeriodChip({ v, cur, setCur, children }) {
  const active = cur === v;
  return (
    <button
      onClick={() => setCur(v)}
      data-testid={`budget-period-${v}`}
      className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider transition ${
        active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
